import * as url from '@/client/services/url'
import {
  getTutorTrainingUrl as getBetaTutorTrainingUrl,
  getScheduleUrl,
  getTutorUrl,
  getUserUrl,
} from '@/client/services/url'

import type { UserDetails } from '@/components/Worlds/types'

import { ACHIEVEMENT_ID_TO_DATA } from './achievements'
import {
  ACHIEVEMENT_NAMES,
  type AchievementCategory,
  type UserEventType,
  type UserProgression,
} from './types'
import { assertUserEventType } from './utils'

export type UserEventProcessor = 'v1-nov-2021'

export const PROCESSOR_VERSION: UserEventProcessor = 'v1-nov-2021'
export const PROGRESSION_DEFAULT: UserProgression = {
  points: 0,

  num_sessions_attended: 0,
  num_sessions_hosted: 0,
  num_users_referred: 0,
  num_certifications_approved: 0,

  achievements: {},
}

export const MIN_SESSION_HOST_POINTS = 3

export const EVENT_DASHBOARD_TITLE: Record<UserEventType['type'], string | null> = {
  welcome: 'Joined Schoolhouse',
  points_earned: null,
  achievement_unlocked: 'Unlocked an Achievement',
  session_attended: 'Attended a Session',
  session_feedback_given: 'Gave Session Feedback',
  session_feedback_received: 'Got Session Feedback',
  session_hosted: 'Hosted a Session',
  referred_user: 'Referred a User',
  referred_by_user: 'Referred by a User',
  certification_approved: 'Completed a Certification',
  tutor_training_completed: 'Tutor Training Completed',
  certification_reviewed: 'Certification Request Reviewed',
  session_audited: 'Session Audited',
  session_safety_audited: 'Completed a Safety Audit',
  learner_feedback_given: 'Gave Learner Feedback',
  learner_feedback_received: 'Got Learner Feedback',
  audit_reply_submitted: 'Replied to Peer Review',
  post_feedback_given: 'Gave Post Feedback',
  post_feedback_received: 'Received Post Feedback',
  verify_peer_reviewer_level: "Verified a Peer Reviewer's Level",
  resolve_coach_escalation: 'Resolved a Coach Escalation',
  learner_account_deleted: null,
}

export const EVENT_NOTIFICATION_TITLE: Record<UserEventType['type'], string | null> = {
  welcome: 'joining the Schoolhouse community!',
  points_earned: null,
  achievement_unlocked: null,
  session_attended: 'attending sessions. Keep it up!',
  session_feedback_given: 'providing feedback.',
  session_feedback_received: 'getting session feedback!',
  session_hosted: 'hosting sessions. Keep it going!',
  referred_user: 'referring friends.',
  referred_by_user: 'joining through a referral.',
  certification_approved: 'getting certified. Nice!',
  tutor_training_completed: 'completing tutor trainings. Stellar!',
  certification_reviewed: 'peer reviewing certifications. Great work!',
  session_audited: 'peer reviewing sessions. Thank you!',
  session_safety_audited: 'conducting session safety audits. Thank you!',
  learner_feedback_given: 'providing learner feedback. Thank you!',
  learner_feedback_received: 'getting feedback!',
  audit_reply_submitted: 'replying to peer reviews. We appreciate it!',
  post_feedback_given: 'providing post feedback.',
  post_feedback_received: 'getting post feedback!',
  verify_peer_reviewer_level: 'verifying peer reviewer levels!',
  resolve_coach_escalation: 'resolving session escalations!',
  learner_account_deleted: null,
}

// This should eventually replace EVENT_NOTIFICATION_URL, but currently we're
// not all the way through migrating pages

export const SUBWORLD_EVENT_NOTIFICATION_URL: Record<
  UserEventType['type'],
  ({ event, user }: { event: UserEventType; user: Pick<UserDetails, 'id' | 'username'> }) => string
> = {
  welcome: ({ user }) =>
    getUserUrl({ userId: user.id, username: user.username, path: 'achievements' }),
  points_earned: ({ user }) =>
    getUserUrl({ userId: user.id, username: user.username, path: 'achievements' }),
  achievement_unlocked: ({ user }) =>
    getUserUrl({ userId: user.id, username: user.username, path: 'achievements' }),
  session_attended: () => getScheduleUrl({ past: true }),
  session_feedback_given: ({ event }) => {
    assertUserEventType(event, 'session_feedback_given')
    return getScheduleUrl({ past: true })
  },
  session_feedback_received: ({ event }) => {
    assertUserEventType(event, 'session_feedback_received')
    const { sessionFeedbackId } = event.payload
    return url.getTutorFeedbackUrl({ sessionFeedbackId })
  },
  session_hosted: () => getScheduleUrl({ past: true }),
  referred_user: () => url.getReferUrl(),
  referred_by_user: () => url.getReferUrl(),
  certification_approved: ({ user }) => getUserUrl({ userId: user.id, username: user.username }),
  tutor_training_completed: () => getBetaTutorTrainingUrl({ completed: true }),
  certification_reviewed: ({ user }) => getUserUrl({ userId: user.id, username: user.username }),
  session_audited: () => getTutorUrl('peer-review'),
  session_safety_audited: () => getTutorUrl('peer-review'),
  learner_feedback_given: ({ event }) => {
    assertUserEventType(event, 'learner_feedback_given')
    const { sessionId } = event
    return getScheduleUrl({ past: true, sessionId })
  },
  learner_feedback_received: ({ event }) => {
    assertUserEventType(event, 'learner_feedback_received')
    const { sessionId } = event
    return getScheduleUrl({ past: true, sessionId })
  },
  audit_reply_submitted: () => url.getTutorFeedbackUrl({}),
  post_feedback_given: ({ event }) => {
    assertUserEventType(event, 'post_feedback_given')
    const { postId } = event.payload
    return url.getPostUrl({ type: 'live-help', postId })
  },
  post_feedback_received: ({ event }) => {
    assertUserEventType(event, 'post_feedback_received')
    const { postId } = event.payload
    return url.getPostUrl({ type: 'live-help', postId })
  },
  verify_peer_reviewer_level: () => url.getAdminUrl('peer-review'),
  resolve_coach_escalation: () => url.getAdminUrl('peer-review/reviews'),
  learner_account_deleted: () => url.getDashboardUrl(),
}

export const MANUALLY_ASSIGNED_ACHIEVEMENTS = ACHIEVEMENT_NAMES.filter(
  aId => ACHIEVEMENT_ID_TO_DATA[aId].category === 'one-off'
)

export const ACHIEVEMENT_CATEGORY_TO_LABEL: Record<AchievementCategory, string> = {
  'one-off': 'Special Badges',
  other: 'Other',
  host: 'Tutored Sessions',
  attend: 'Attended Sessions',
  certify: 'Certifications Completed',
  refer: 'Friends Referred',
  level: 'Schoolhouse Points',
}
